import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Keyboard, 
  KeyboardOff, 
  ArrowLeft, 
  Check,
  Lock,
  ChevronUp
} from 'lucide-react';

const VirtualKeyboard = ({ 
  onInput, 
  onClear, 
  onClose, 
  targetInput,
  layout = 'standard',
  showNumbers = true,
  showSymbols = false,
  className = ''
}) => {
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const [currentLayout, setCurrentLayout] = useState('letters');
  const keyboardRef = useRef(null);

  // Keyboard layouts
  const keyboardLayouts = {
    letters: {
      rows: [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        ['z', 'x', 'c', 'v', 'b', 'n', 'm']
      ]
    },
    numbers: {
      rows: [
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
        ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
        ['.', ',', '?', '!', "'", '_']
      ]
    },
    symbols: {
      rows: [
        ['[', ']', '{', '}', '#', '%', '^', '*', '+', '='],
        ['_', '\\', '|', '~', '<', '>', '€', '£', '¥', '•'],
        ['.', ',', '?', '!', "'", '_']
      ]
    }
  };

  const getCharacter = useCallback((char) => {
    if (currentLayout === 'letters') {
      if (isShiftPressed || isCapsLockOn) {
        return char.toUpperCase();
      }
      return char.toLowerCase();
    }
    return char;
  }, [currentLayout, isShiftPressed, isCapsLockOn]);

  const handleKeyPress = useCallback((char) => {
    if (onInput && targetInput) {
      const processedChar = getCharacter(char);
      onInput(processedChar);
      
      // Reset shift after typing a letter
      if (currentLayout === 'letters' && isShiftPressed) {
        setIsShiftPressed(false);
      }
    }
  }, [onInput, targetInput, getCharacter, currentLayout, isShiftPressed]);

  const handleBackspace = useCallback(() => {
    if (onInput && targetInput) {
      onInput('BACKSPACE');
    }
  }, [onInput, targetInput]);

  const handleSpace = useCallback(() => {
    if (onInput && targetInput) {
      onInput(' ');
    }
  }, [onInput, targetInput]);

  const handleEnter = useCallback(() => {
    if (onInput && targetInput) {
      onInput('ENTER');
    }
  }, [onInput, targetInput]);

  const handleShift = useCallback(() => {
    setIsShiftPressed(!isShiftPressed);
  }, [isShiftPressed]);

  const handleCapsLock = useCallback(() => {
    setIsCapsLockOn(!isCapsLockOn);
  }, [isCapsLockOn]);

  const switchLayout = useCallback((layout) => {
    setCurrentLayout(layout);
    setIsShiftPressed(false);
  }, []);

  // Position keyboard near target input
  useEffect(() => {
    if (targetInput && keyboardRef.current) {
      const inputRect = targetInput.getBoundingClientRect();
      const keyboardRect = keyboardRef.current.getBoundingClientRect();
      
      let top = inputRect.bottom + 10;
      let left = inputRect.left;
      
      // Adjust if keyboard goes off screen
      if (top + keyboardRect.height > window.innerHeight) {
        top = inputRect.top - keyboardRect.height - 10;
      }
      
      if (left + keyboardRect.width > window.innerWidth) {
        left = window.innerWidth - keyboardRect.width - 10;
      }
      
      if (left < 10) {
        left = 10;
      }
      
      keyboardRef.current.style.position = 'fixed';
      keyboardRef.current.style.top = `${top}px`;
      keyboardRef.current.style.left = `${left}px`;
      keyboardRef.current.style.zIndex = '9999';
    }
  }, [targetInput]);

  const renderKey = (char, isSpecial = false, icon = null, onClick = null, className = '') => {
    const baseClasses = 'relative flex items-center justify-center rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-primary-900 dark:text-white font-medium text-sm transition-all hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 min-h-[40px] px-3 py-2 cursor-pointer select-none';
    
    return (
      <button
        type="button"
        className={`${baseClasses} ${className}`}
        onClick={onClick || (() => handleKeyPress(char))}
        onMouseDown={(e) => e.preventDefault()}
      >
        {icon || <span className="text-sm">{char}</span>}
      </button>
    );
  };

  const renderKeyboard = () => {
    const layout = keyboardLayouts[currentLayout];
    
    return (
      <div className="flex flex-col gap-2 p-3">
        {layout.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-1 justify-center">
            {row.map((key, keyIndex) => (
              <div key={keyIndex} className="flex-1 max-w-[45px]">
                {renderKey(key)}
              </div>
            ))}
          </div>
        ))}
        
        {/* Special keys row */}
        <div className="flex gap-1 justify-center mt-2">
          {currentLayout === 'letters' && (
            <>
              <div className="flex-1 max-w-[60px]">
                {renderKey('Caps', true, <Lock size={16} />, handleCapsLock, 
                  isCapsLockOn ? 'bg-primary-500 text-white border-primary-600' : '')}
              </div>
              <div className="flex-1 max-w-[60px]">
                {renderKey('Shift', true, <ChevronUp size={16} />, handleShift,
                  isShiftPressed ? 'bg-primary-500 text-white border-primary-600' : '')}
              </div>
            </>
          )}
          
          <div className="flex-1 max-w-[80px]">
            {renderKey('←', true, <ArrowLeft size={16} />, handleBackspace)}
          </div>
          
          <div className="flex-1 max-w-[120px]">
            {renderKey('Space', false, null, handleSpace, 'px-4')}
          </div>
          
          <div className="flex-1 max-w-[60px]">
            {renderKey('Enter', true, <Check size={16} />, handleEnter)}
          </div>
        </div>
        
        {/* Layout switcher */}
        <div className="flex gap-1 justify-center mt-2 pt-2 border-t border-primary-200 dark:border-slate-700">
          {showNumbers && (
            <div className="flex-1 max-w-[60px]">
              {renderKey('123', false, null, () => switchLayout('numbers'),
                currentLayout === 'numbers' ? 'bg-primary-500 text-white border-primary-600' : '')}
            </div>
          )}
          
          {showSymbols && (
            <div className="flex-1 max-w-[60px]">
              {renderKey('#@!', false, null, () => switchLayout('symbols'),
                currentLayout === 'symbols' ? 'bg-primary-500 text-white border-primary-600' : '')}
            </div>
          )}
          
          <div className="flex-1 max-w-[60px]">
            {renderKey('ABC', false, null, () => switchLayout('letters'),
              currentLayout === 'letters' ? 'bg-primary-500 text-white border-primary-600' : '')}
          </div>
        </div>
      </div>
    );
  };

  if (!targetInput) return null;

  return (
    <div
      ref={keyboardRef}
      className={`bg-primary-50 dark:bg-slate-900 rounded-xl shadow-2xl border border-primary-200 dark:border-slate-700 ${className}`}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-primary-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Keyboard size={16} className="text-primary-700 dark:text-primary-300" />
          <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
            Virtual Keyboard
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          onMouseDown={(e) => e.preventDefault()}
        >
          <KeyboardOff size={16} />
        </button>
      </div>
      
      {/* Keyboard */}
      {renderKeyboard()}
    </div>
  );
};

export default VirtualKeyboard;
